import { useQuery } from "@tanstack/react-query";
import { createAuthClient } from "better-auth/react";
import { authQueries } from "~/lib/queries";
import { inferAdditionalFields } from "better-auth/client/plugins";
import type { auth } from "~/lib/better-auth/auth";

// Determine the correct base URL based on the current domain
function getBaseURL(): string {
  if (typeof window === "undefined") {
    // Server-side: use base URL as fallback
    return import.meta.env.VITE_BASE_URL;
  }

  // Client-side: check if we're on the admin domain
  const isAdminDomain =
    window.location.hostname.startsWith(import.meta.env.VITE_ADMIN_HOST);

  return isAdminDomain
    ? import.meta.env.VITE_ADMIN_URL
    : import.meta.env.VITE_BASE_URL;
}

export const authClient = createAuthClient({
  baseURL: getBaseURL(),
  plugins: [inferAdditionalFields<typeof auth>()],
});

export const useAuthentication = () => {
  const { data: userSession } = useQuery(authQueries.user());

  return { userSession, isAuthenticated: !!userSession };
};

export const useAuthenticatedUser = () => {
  const { userSession } = useAuthentication();

  if (!userSession) {
    throw new Error("User is not authenticated!");
  }

  return userSession;
};

/**
 * Custom error class for expected authentication failures.
 * These are user errors (wrong password, user not found, etc.) that should
 * be displayed to users but not logged to error monitoring tools like Sentry.
 */
export class ExpectedAuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ExpectedAuthError";
    // Maintains proper stack trace for where our error was thrown (only on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ExpectedAuthError);
    }
  }
}

/**
 * Determines if an error represents an expected authentication failure
 * (wrong password, invalid credentials, user not found, etc.)
 * vs an unexpected error (network failure, server error, etc.)
 */
function isExpectedAuthError(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }

  // Check for better-auth error codes that indicate expected failures
  if ("code" in error) {
    const expectedCodes = [
      "INVALID_CREDENTIALS",
      "INVALID_PASSWORD",
      "USER_NOT_FOUND",
      "EMAIL_NOT_VERIFIED",
      "ACCOUNT_LOCKED",
      "TOO_MANY_ATTEMPTS",
      "USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL",
    ];
    if (typeof error.code === "string" && expectedCodes.includes(error.code)) {
      return true;
    }
  }

  // Check for HTTP status codes that indicate expected client errors
  if ("status" in error && typeof error.status === "number") {
    // 400-499 are client errors (expected user errors)
    // 500+ are server errors (unexpected)
    if (error.status >= 400 && error.status < 500) {
      return true;
    }
  }

  // Check error message patterns for expected failures
  if ("message" in error && typeof error.message === "string") {
    const message = error.message.toLowerCase();
    const expectedPatterns = [
      "invalid",
      "wrong password",
      "incorrect password",
      "user not found",
      "email not found",
      "account not found",
      "credentials",
      "unauthorized",
      "not verified",
      "already exists",
    ];
    if (expectedPatterns.some((pattern) => message.includes(pattern))) {
      return true;
    }
  }

  return false;
}

/**
 * Extracts a user-friendly error message from a better-auth error object.
 * Handles various error formats that better-auth might return.
 * 
 * @param error - The error object from better-auth
 * @param fallbackMessage - The fallback message if no error details can be extracted
 * @returns A string error message
 */
const AUTH_ERROR_MESSAGES_DE: Record<string, string> = {
  "Invalid token": "Ungültiger oder abgelaufener Link.",
  "Password too short": "Passwort ist zu kurz.",
  "Password too long": "Passwort ist zu lang.",
  "Invalid password": "Ungültiges Passwort.",
  "Invalid email": "Ungültige E-Mail-Adresse.",
  "Invalid email or password": "Ungültige E-Mail-Adresse oder Passwort.",
  "User not found": "Benutzer nicht gefunden.",
  "Email not verified": "E-Mail-Adresse wurde noch nicht bestätigt.",
  "User already exists.": "Benutzer existiert bereits.",
  "User already exists. Use another email.":
    "Benutzer existiert bereits. Bitte nutze eine andere E-Mail-Adresse.",
};

/**
 * Maps known better-auth English messages to German for display.
 * Call only when rendering — keep English discriminators on Error.message
 * so callers can still compare against messages like "Email not verified".
 */
export function localizeAuthErrorMessage(message: string): string {
  return AUTH_ERROR_MESSAGES_DE[message] ?? message;
}

export function extractAuthErrorMessage(
  error: unknown,
  fallbackMessage: string = "Ein Fehler ist aufgetreten. Bitte versuche es erneut."
): string {
  // If error is null or undefined, return fallback
  if (!error) {
    return fallbackMessage;
  }

  // If error is a string, use it directly (English discriminator preserved)
  if (typeof error === "string") {
    return error;
  }

  // If error is an object, try to extract message
  if (typeof error === "object") {
    // Check for message property — keep original English for callers that branch on it
    if ("message" in error && typeof error.message === "string" && error.message.trim()) {
      return error.message;
    }

    // Check for statusText (common in HTTP errors)
    if ("statusText" in error && typeof error.statusText === "string" && error.statusText.trim()) {
      return error.statusText;
    }

    // Check for status and construct a message
    if ("status" in error && typeof error.status === "number") {
      const statusMessage = "statusText" in error && typeof error.statusText === "string"
        ? error.statusText
        : `Error ${error.status}`;
      return statusMessage;
    }

    // Try to stringify the error object for debugging
    try {
      const errorString = JSON.stringify(error);
      if (errorString && errorString !== "{}") {
        return errorString;
      }
    } catch {
      // If stringification fails, continue to fallback
    }
  }

  // Fallback to generic message
  return fallbackMessage;
}

/**
 * Determines if an error is a network/system error that should be logged.
 * These are errors that indicate infrastructure problems, not user mistakes.
 */
function isSystemError(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }

  // Server errors (500+) are always system errors
  if ("status" in error && typeof error.status === "number" && error.status >= 500) {
    return true;
  }

  // Network errors (no status, or network-related error types)
  const errorName = "name" in error && typeof error.name === "string" ? error.name.toLowerCase() : "";
  const networkErrorNames = ["networkerror", "typeerror", "aborterror", "timeouterror"];
  if (networkErrorNames.some((name) => errorName.includes(name))) {
    return true;
  }

  // Check for network-related error messages
  if ("message" in error && typeof error.message === "string") {
    const message = error.message.toLowerCase();
    const systemErrorPatterns = [
      "network",
      "timeout",
      "connection",
      "failed to fetch",
      "network request failed",
      "cors",
      "aborted",
      "refused",
    ];
    if (systemErrorPatterns.some((pattern) => message.includes(pattern))) {
      return true;
    }
  }

  // If error has no recognizable structure and no status, it might be a system error
  // But we'll be conservative - only treat as system error if it has error-like properties
  // but no status code (suggests network/transport issue)
  const hasStatus = "status" in error;
  const hasCode = "code" in error;
  const hasMessage = "message" in error && typeof (error as any).message === "string";
  
  // If it's an error-like object but has no status/code, might be a network error
  // But we need to be careful - better-auth might return errors without status
  // So we only treat as system error if it looks like a network error specifically
  if (!hasStatus && !hasCode && hasMessage) {
    const message = ((error as any).message as string).toLowerCase();
    if (message.includes("fetch") || message.includes("network") || message.includes("timeout")) {
      return true;
    }
  }

  return false;
}

/**
 * Creates an appropriate error for authentication failures.
 * Returns ExpectedAuthError for expected failures (won't be logged to Sentry)
 * and regular Error for unexpected failures (will be logged).
 * 
 * @param error - The error object from better-auth
 * @param fallbackMessage - The fallback message if no error details can be extracted
 * @param operationType - The type of auth operation (sign-in, sign-up, etc.) to help determine if error is expected
 * @returns An Error or ExpectedAuthError instance
 */
export function createAuthError(
  error: unknown,
  fallbackMessage: string = "Ein Fehler ist aufgetreten. Bitte versuche es erneut.",
  operationType: "sign-in" | "sign-up" | "password-reset" | "password-change" | "user-update" | "other" = "other"
): Error {
  const message = extractAuthErrorMessage(error, fallbackMessage);
  
  // Always log system errors (network issues, server errors, etc.)
  if (isSystemError(error)) {
    return new Error(message);
  }
  
  // For authentication operations (sign-in, sign-up), treat errors as expected
  // unless they're system errors. This prevents spamming error logs with common
  // user errors like wrong passwords, while still catching infrastructure issues.
  const isAuthOperation = ["sign-in", "sign-up", "password-reset", "password-change"].includes(operationType);
  
  // Use ExpectedAuthError for:
  // 1. Errors that are explicitly identified as expected
  // 2. Auth operation errors that aren't system errors (wrong password, user not found, etc.)
  if (isExpectedAuthError(error) || (isAuthOperation && !isSystemError(error))) {
    return new ExpectedAuthError(message);
  }
  
  // Use regular Error for unexpected failures (for non-auth operations, or edge cases)
  return new Error(message);
}
